/**
 * The recipe editor — what a dish consumes per portion.
 *
 * The number this produces is not stock. A recipe dish holds nothing of its
 * own; what its tile shows is how many PORTIONS its ingredients allow, and
 * the cloud works that out whenever an ingredient moves. The "allows N
 * portions today" lines here are a preview while the recipe is being typed —
 * the cloud cannot have an opinion about a recipe that is not saved yet.
 *
 * Quantities are entered in the unit people think in — 200 g of rice, not
 * 0.2 kg — and stored in the ingredient's own unit, because every ledger
 * quantity for that ingredient is in it.
 *
 * See docs/feature-stock-intake-and-suppliers.md §7.4 and base spec §12.8.
 */
import React, {useMemo, useState} from 'react';
import {
  entryUnitsFor,
  fromBaseQty,
  portionsForLine,
  recipePreview,
  toBaseQty,
  validateRecipe,
  type IngredientLike,
  type RecipeLine,
} from '../../../internals/managers/RecipeManager';

interface Props {
  recipe: RecipeLine[];
  ingredients: IngredientLike[];
  onChange: (recipe: RecipeLine[]) => void;
}

const inputCls = 'nl-input nl-input--sm';
const selectCls = 'nl-select nl-input--sm';

const RecipeEditor = ({recipe, ingredients, onChange}: Props) => {
  // Memoised: `recipe ?? []` would be a NEW array on every render when the
  // prop is null, so the previews below would recompute each keystroke.
  const lines = useMemo(() => recipe ?? [], [recipe]);
  const [search, setSearch] = useState('');

  const byId = useMemo(() => {
    const map: Record<string, IngredientLike> = {};
    for (const i of ingredients ?? []) map[i.stockId] = i;
    return map;
  }, [ingredients]);

  /**
   * Which unit each row is being typed in. Defaults to the smaller one — a
   * recipe is far more often grams than kilograms.
   */
  const [entryUnits, setEntryUnits] = useState<Record<string, string>>({});

  const unitFor = (line: RecipeLine) => {
    const base = byId[line.stockId]?.metricValue;
    return entryUnits[line.stockId] ?? entryUnitsFor(base)[0].unit;
  };

  const preview = useMemo(() => recipePreview(lines, byId), [lines, byId]);
  const problems = useMemo(() => validateRecipe(lines), [lines]);
  const problemAt = (index: number) =>
    problems.find((p) => p.index === index && p.index >= 0)?.error;

  const available = (ingredients ?? [])
    .filter((i) => !lines.some((l) => l.stockId === i.stockId))
    .filter((i) => !search || i.itemName.toLowerCase().includes(search.toLowerCase()));

  const addLine = (stockId: string) => {
    if (!stockId) return;
    onChange([...lines, {stockId, qtyPerPortion: 0, unit: byId[stockId]?.metricValue}]);
    setSearch('');
  };

  const setQty = (stockId: string, typed: string) => {
    const base = byId[stockId]?.metricValue;
    const qtyPerPortion = toBaseQty(typed, unitFor({stockId} as RecipeLine), base);
    onChange(
      lines.map((l) => (l.stockId === stockId ? {...l, qtyPerPortion, unit: base} : l)),
    );
  };

  const switchUnit = (stockId: string, unit: string) =>
    setEntryUnits((u) => ({...u, [stockId]: unit}));

  const removeLine = (stockId: string) =>
    onChange(lines.filter((l) => l.stockId !== stockId));

  return (
    <div className="mb-3 rounded-lg border border-borderc p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-medium text-text1">Recipe</span>
        <span className="text-xs text-text3">per portion</span>
      </div>

      {lines.length === 0 && (
        <p className="mb-2 text-xs text-text3">
          Add the ingredients one portion uses. Until at least one is added,
          this dish is not tracked.
        </p>
      )}

      {lines.map((line, index) => {
        const ingredient = byId[line.stockId];
        const base = ingredient?.metricValue;
        const unit = unitFor(line);
        const units = entryUnitsFor(base);
        const allows = portionsForLine(line, ingredient);
        const err = problemAt(index);

        return (
          <div key={line.stockId} className="mb-2 border-b border-borderc pb-2 last:border-0">
            <div className="flex items-center gap-2">
              <span className="flex-1 truncate text-sm text-text1">
                {ingredient?.itemName ?? (
                  <span className="text-amber-700">
                    Ingredient no longer available
                  </span>
                )}
              </span>
              <input
                type="number"
                min="0"
                step="any"
                value={
                  line.qtyPerPortion
                    ? fromBaseQty(line.qtyPerPortion, unit, base)
                    : ''
                }
                onChange={(e) => setQty(line.stockId, e.target.value)}
                className={`${inputCls} w-24 text-right tabular-nums`}
                placeholder="0"
              />
              {units.length > 1 ? (
                <select
                  value={unit}
                  onChange={(e) => switchUnit(line.stockId, e.target.value)}
                  className={`${selectCls} w-20`}
                >
                  {units.map((u) => (
                    <option key={u.unit} value={u.unit}>
                      {u.unit}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="w-20 text-xs text-text3">{unit}</span>
              )}
              <button
                type="button"
                onClick={() => removeLine(line.stockId)}
                className="px-1 text-text3 hover:text-red-600"
                title="Remove"
              >
                ✕
              </button>
            </div>
            <div className="mt-1 flex justify-between pl-0.5">
              <span className="text-[11px] text-text3">
                {allows === null
                  ? ingredient && line.qtyPerPortion > 0
                    ? 'ingredient not counted yet'
                    : ''
                  : `allows ${allows} portion${allows === 1 ? '' : 's'} today`}
              </span>
              {err && <span className="text-[11px] text-red-600">{err}</span>}
            </div>
          </div>
        );
      })}

      <div className="mt-2 flex gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ingredients…"
          className={`${inputCls} flex-1 w-auto`}
        />
        <select
          value=""
          onChange={(e) => addLine(e.target.value)}
          className={`${selectCls} w-36`}
        >
          <option value="">Add…</option>
          {available.map((i) => (
            <option key={i.stockId} value={i.stockId}>
              {i.itemName} ({i.metricValue})
            </option>
          ))}
        </select>
      </div>

      {(ingredients ?? []).length === 0 && (
        <p className="mt-2 text-[11px] text-text3">
          No ingredients yet. Add them on the Stock&nbsp;Levels screen, under
          Ingredients.
        </p>
      )}

      {lines.length > 0 && (
        <div className="mt-3 rounded-lg bg-surface2 px-3 py-2">
          {preview.tracked ? (
            <p className="text-xs text-text1">
              Portions possible: <strong>{preview.portions}</strong>
              {preview.limitingName && (
                <span className="text-text3"> · limited by {preview.limitingName}</span>
              )}
            </p>
          ) : (
            <p className="text-xs text-text3">
              None of these ingredients has been counted yet, so this dish stays
              untracked until one is.
            </p>
          )}
          <p className="mt-0.5 text-[11px] text-text3">
            A preview — the tills get this figure from the cloud.
          </p>
        </div>
      )}
    </div>
  );
};

export default RecipeEditor;
